<template>
  <div
    class="ak-overlay"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
  >
    <slot />
    <transition name="fade">
      <div v-show="showOverlay" class="ak-overlay__el" :class="classNames">
        <ak-box size="tiny">
          <slot name="overlayBody" />
        </ak-box>
      </div>
    </transition>
  </div>
</template>

<script>
import { computed, ref, defineComponent } from 'vue'
import Box from '../box/Box.vue'

export default defineComponent({
  /**
   * @type {String}
   * @description This is the name of the component this will also
   * be the tag name of the component to use in templates
   * @example
   * <ak-overlay :inverse=true></ak-pack>
   */
  name: 'AkOverlay',

  /**
   * @type {Object}
   * @description A collection of components that this component
   * is dependant on.
   */
  components: {
    [Box.name]: Box,
  },

  /**
   * @type {Object}
   * @description A collection of properties that may be passed into
   * the component these will be bound to the component instance and
   * may be access in the template and component methods.
   */
  props: {
    /**
     * @type {Boolean}
     * @description Controls whether the overlay will be light rather than dark
     */
    isLight: {
      type: Boolean,
      default: false,
    },
    isPrimary: {
      type: Boolean,
      default: false,
    },
    hasRadius: {
      type: Boolean,
      default: false,
    },
  },

  /**
   * @type {Array}
   * @description The list of events that this component can trigger
   */
  emits: ['overlayShown', 'overlayHidden'],

  /**
   * @param {Object} props - The properties of the component
   * @param {Object} options - The setup options
   * @param {Function} options.emit - The emit method for triggering events
   * @description Sets up the component and returns an object containing all of the
   * attributes for the component to use.
   */
  setup(props, { emit }) {
    const showOverlay = ref(false)

    const handleMouseEnter = () => {
      showOverlay.value = true
      emit('overlayShown')
    }

    const handleMouseLeave = () => {
      showOverlay.value = false
      emit('overlayHidden')
    }

    const classNames = computed(() => {
      return {
        'ak-overlay__el--light': props.isLight,
        'ak-overlay__el--primary': props.isPrimary,
        radius: props.hasRadius,
      }
    })

    return {
      handleMouseEnter,
      handleMouseLeave,
      showOverlay,
      classNames,
    }
  },
})
</script>

<style lang="scss">
@import '../../scss/_settings.scss';
.ak-overlay {
  position: relative;

  &__el {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: rgba($color-grey-dark, 0.75);
    backdrop-filter: blur(5px);
    color: #fff;

    &--light {
      background-color: rgba(255, 255, 255, 0.75);
      color: $color-text;
    }
    &--primary {
      background-color: $color-primary;
      color: #fff;
    }
  }
}
</style>
